/**
 * "I've forgotten my password."
 *
 * The confirmation is deliberately vague — "if an account exists, a link is on
 * its way" — because the API refuses to say whether the address is registered.
 * Confirming it here would undo that: anyone could type addresses in and read
 * the screen to learn which clinics have accounts.
 */
import { useState, type FormEvent } from 'react';
import { forgotPassword } from '../lib/api.js';
import { c, primaryButton } from '../theme.js';
import { AuthLayout, Field, NoticeBox, TextLink } from './AuthLayout.js';

export function ForgotPasswordScreen({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    // No error branch: this endpoint always succeeds by design. A network
    // failure is the only way to get here and it would be indistinguishable
    // from a delivery delay anyway.
    await forgotPassword(email).catch(() => undefined);
    setBusy(false);
    setSent(true);
  }

  if (sent) {
    return (
      <AuthLayout heading="Check your email">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <NoticeBox>
            If an account exists for <strong>{email}</strong>, a link to choose a new password is on
            its way. It lasts one hour and can only be used once.
          </NoticeBox>
          <p style={{ fontSize: 13, color: c.muted, margin: 0, lineHeight: 1.6 }}>
            Nothing arrived? Check spam, then try again. Requesting a new link retires the previous
            one.
          </p>
          <div>
            <TextLink onClick={onBack}>← Back to sign in</TextLink>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      heading="Forgotten your password?"
      intro="Enter the email you sign in with and we’ll send a link to reset it."
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          id="email"
          labelText="Email"
          type="email"
          placeholder="you@business.co.uk"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          autoFocus
        />
        <button
          type="submit"
          disabled={busy}
          style={{ ...primaryButton, marginTop: 4, opacity: busy ? 0.65 : 1 }}
        >
          {busy ? 'Sending…' : 'Send the reset link'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 2 }}>
          <TextLink onClick={onBack}>← Back to sign in</TextLink>
        </div>
      </form>
    </AuthLayout>
  );
}
